/**
 * Arriving from the Tab Service portal.
 *
 * The portal signs a person in, asks the API for a one-time ticket, and sends the browser
 * here with it after the `#`. The fragment never reaches a server or a log, and it is wiped
 * from the address bar before anything else happens, so a screenshot or a shared tab does
 * not carry it. The ticket is then traded for a session and the person lands on their
 * e-files, or wherever the portal said, without typing anything.
 *
 * Anything that goes wrong lands on the sign-in page with one sentence, because the fix is
 * always the same: go back to the portal and come across again.
 */
import { useEffect, useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { TabFileError } from '@tabservice/tabfile-client';
import { Button } from '@/components/ui/button';
import { ErrorNote } from '@/components/ui/feedback';
import { HandoverScreen } from '@/components/handover-screen';
import { authStore } from '@/lib/auth-store';
import { rememberPortalAccount } from '@/lib/portal-account';
import { errorMessage } from '@/lib/api';
import { hasCustomer, isStaff, meQuery } from '@/lib/queries';

export const Route = createFileRoute('/sso')({
  component: SsoPage,
  // This page draws its own quiet waiting state; the default skeleton would flash a page of
  // grey blocks in front of it first.
  pendingComponent: () => null,
});

/**
 * What the portal sent, taken out of the address bar as it is read.
 *
 * `t` is the one-time ticket. `p=1` says this person has a portal account as well, which the
 * portal only adds when they came from its own E-Filing entry; somebody who has e-filing and
 * nothing else arrives without it. It decides one thing: whether the account menu offers a
 * way back. It grants nothing, and the portal still decides what they may do when they get
 * there.
 */
function takeHandover(): { ticket: string | null; portalAccount: boolean } {
  const hash = window.location.hash;
  if (!hash) return { ticket: null, portalAccount: false };
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const ticket = params.get('t');
  const portalAccount = params.get('p') === '1';
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return { ticket: ticket && ticket.trim() ? ticket.trim() : null, portalAccount };
}

/** A path on this app, or the front door. The portal never gets to send someone elsewhere. */
function safeDestination(returnTo: string | null | undefined): string {
  if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) return '/';
  return returnTo;
}

function SsoPage() {
  const navigate = useNavigate();
  const { queryClient } = Route.useRouteContext();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const { ticket, portalAccount } = takeHandover();
    if (!ticket) {
      setError('This address is incomplete. Sign in again.');
      return;
    }
    rememberPortalAccount(portalAccount);
    (async () => {
      try {
        const returnTo = await authStore.signInFromPortal(ticket);
        if (cancelled) return;
        // Whatever the previous session had fetched is not this person's.
        queryClient.clear();
        const me = await queryClient.fetchQuery({
          ...meQuery(),
          retry: (failureCount, err) => !(err instanceof TabFileError) && failureCount < 4,
          retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
        });
        if (cancelled) return;
        if (isStaff(me) && !hasCustomer(me)) {
          await navigate({ to: '/admin', replace: true });
          return;
        }
        await navigate({ to: safeDestination(returnTo), replace: true });
      } catch (err) {
        if (cancelled) return;
        // Roll back, the same way the sign-in page does: a Firebase session with no e-filing
        // behind it would only reopen the shell to a refused request.
        await authStore.signOut().catch(() => undefined);
        setError(errorMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, queryClient]);

  // Nothing is drawn while it works, not even a spinner on a card: the crossing is our
  // plumbing, and a card that appears and vanishes is the announcement itself. Words appear
  // only when it fails, because then there is something to act on.
  if (!error) return null;

  return (
    <HandoverScreen title="Could not sign you in" detail="Sign in again to continue.">
      <ErrorNote title="Sign-in did not finish" detail={error} />
      <Button asChild variant="primary" size="lg" className="w-full">
        <Link to="/sign-in">Sign in</Link>
      </Button>
    </HandoverScreen>
  );
}
